import React, { useEffect, useState } from "react";
import axios from "axios";
import { useCountUp } from "./useCountUp";
import { SkeletonHero } from "./Skeleton";
import { fmtNum, fmtCurrency, fmtPct, fmtDuration, sourceBadge } from "./format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AnimatedCurrency = ({ value, currency }) => {
  const v = useCountUp(value, { duration: 1100 });
  return <span>{fmtCurrency(v, currency)}</span>;
};

const AnimatedNumber = ({ value, decimals = 0 }) => {
  const v = useCountUp(value, { duration: 1100, decimals });
  return <span>{fmtNum(v, { decimals })}</span>;
};

const AnimatedPct = ({ value }) => {
  const v = useCountUp(value, { duration: 1100, decimals: 4 });
  return <span>{fmtPct(v)}</span>;
};

const KpiTileMini = ({ label, value, source, sub, benchmark }) => {
  const badge = sourceBadge(source);
  // benchmark shape: {benchmark, unit, label, vertical_used, delta_pct, status, verdict}
  const bmColor = benchmark?.verdict === "good" ? "#00E676"
                 : benchmark?.verdict === "bad" ? "#FF6D00"
                 : "#A1A1AA";
  const bmText = benchmark
    ? benchmark.status === "on_par"
      ? "On par with industry"
      : `${benchmark.delta_pct > 0 ? "+" : ""}${benchmark.delta_pct}% vs industry`
    : null;
  return (
    <div className="px-3 py-2 rounded bg-white/[0.03] border border-white/10 min-w-[120px] fade-up">
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <span className="text-[9px] uppercase tracking-wider text-white/50">{label}</span>
        {badge && (
          <span className="text-[8px] px-1 py-0.5 rounded font-semibold" style={{ background: badge.bg, color: badge.color }}>
            {badge.label}
          </span>
        )}
      </div>
      <div className="text-sm font-mono text-white">{value}</div>
      {sub && <div className="text-[9px] text-white/40">{sub}</div>}
      {bmText && (
        <div className="text-[9px] mt-1 font-semibold" style={{ color: bmColor }} data-testid="kpi-benchmark-pill">
          {bmText}
        </div>
      )}
    </div>
  );
};

const InsightCard = ({ kind, data, accent, icon, footerLabel, footerKey, testid }) => {
  if (!data) return null;
  let footerValue = data[footerKey];
  if (footerKey === "_recommendation_footer") {
    const e = data.effort, p = data.priority;
    footerValue = (e || p) ? `${e ? e + " effort" : ""}${e && p ? " · " : ""}${p || ""}` : null;
  }
  return (
    <div
      className="relative p-4 rounded-xl border border-white/10 bg-white/[0.025] overflow-hidden"
      style={{ borderTop: `2px solid ${accent}` }}
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="w-6 h-6 rounded flex items-center justify-center font-bold text-sm"
          style={{ background: `${accent}22`, color: accent }}
        >
          {icon}
        </span>
        <span className="text-[10px] uppercase tracking-wider text-white/50">{kind}</span>
      </div>
      <div className="text-sm font-semibold text-white mb-1.5 leading-snug">{data.title}</div>
      <p className="text-[11px] text-white/65 leading-relaxed mb-2">{data.detail}</p>
      {footerValue && (
        <div className="flex items-center gap-1.5 text-[10px] text-white/50">
          <span>{footerLabel}:</span>
          <span style={{ color: accent }} className="font-semibold">{footerValue}</span>
        </div>
      )}
    </div>
  );
};

const PerformanceHero = ({ clientId, clientName, month, monthLabel, currency, kpi, loadingKpi, dateRange, intel, loadingIntel }) => {
  // intel comes from PerformanceIntelligence (single fetch shared by all widgets).
  // No internal fetch — keeps the AI engine call to one round-trip per range.

  // Per-client logo from branding (super_admin uploads via Client edit page).
  // Falls back to nothing if no logo configured — we just show the text header.
  const [clientLogo, setClientLogo] = useState("");
  useEffect(() => {
    if (!clientId) return;
    let alive = true;
    axios.get(`${API}/branding/client/${clientId}`)
      .then((res) => {
        if (alive) setClientLogo(res.data?.branding?.logo_base64 || "");
      })
      .catch(() => { if (alive) setClientLogo(""); });
    return () => { alive = false; };
  }, [clientId]);

  if (loadingKpi) return <SkeletonHero />;

  const sh = kpi?.shopify || {};
  const meta = kpi?.meta || {};
  const ga = kpi?.ga4 || {};
  const bl = kpi?.blended || {};
  const cur = sh.currency || currency || "INR";

  const headline = intel?.headline;
  const summary = intel?.executive_summary;
  // Lookup map for engine-provided benchmark pills (Shopify/Meta metrics)
  const bm = (kpi_deltas => {
    const m = {};
    (kpi_deltas || []).forEach(k => { if (k.benchmark) m[k.metric] = k.benchmark; });
    return m;
  })(intel?.kpi_deltas);

  // Client-side GA4 benchmark lookup (CR / Bounce / Engagement)
  // — backend engine doesn't refetch GA4 numbers since the executive-kpi
  // endpoint already supplies them in `kpi.ga4`.
  const vertical = intel?.vertical || "Fashion/Apparel";
  const GA4_BENCHMARKS = {
    conversion_rate: { "Fashion/Apparel": 2.1, "Beauty": 3.2, "Home": 1.8, "Electronics": 1.4, "Food": 4.0, "Wellness": 2.5, "Jewelry": 0.9, "Other": 2.2 },
    bounce_rate:     { "Fashion/Apparel": 41,  "Beauty": 38,  "Home": 45,  "Electronics": 47,  "Food": 36,  "Wellness": 42,  "Jewelry": 44,  "Other": 42 },
    engagement_rate: { "Fashion/Apparel": 59,  "Beauty": 62,  "Home": 55,  "Electronics": 53,  "Food": 64,  "Wellness": 58,  "Jewelry": 56,  "Other": 58 },
  };
  const ga4Benchmark = (metric, value, lowerBetter = false) => {
    if (value == null) return null;
    // GA4 returns rates as 0-1 fractions; convert to percent for comparison
    const v = value <= 1 ? value * 100 : value;
    const benches = GA4_BENCHMARKS[metric] || {};
    const b = benches[vertical] || benches["Fashion/Apparel"];
    if (b == null) return null;
    const delta = ((v - b) / b) * 100;
    const abs_d = Math.abs(delta);
    let status, verdict;
    if (abs_d < 5) { status = "on_par"; verdict = "neutral"; }
    else if (delta > 0) { status = "above"; verdict = lowerBetter ? "bad" : "good"; }
    else { status = "below"; verdict = lowerBetter ? "good" : "bad"; }
    return { benchmark: b, delta_pct: Math.round(delta * 10) / 10, status, verdict };
  };

  return (
    <section
      className="pi-hero relative p-6 md:p-10 rounded-2xl overflow-hidden fade-up"
      style={{
        background: "radial-gradient(ellipse at top left, rgba(255,235,0,0.08), transparent 60%), radial-gradient(ellipse at bottom right, rgba(184,255,107,0.06), transparent 60%), linear-gradient(135deg, #0F0F14 0%, #08080C 100%)",
        border: "1px solid rgba(255,255,255,0.08)",
      }}
      data-testid="pi-hero"
    >
      <div className="absolute inset-0 pointer-events-none opacity-30" style={{
        background: "radial-gradient(circle at 80% 20%, rgba(255,235,0,0.10), transparent 40%)",
      }} />
      <div className="relative z-10">
        {clientLogo && (
          <div className="mb-4 flex items-center" data-testid="pi-hero-client-logo">
            <img
              src={clientLogo}
              alt={`${clientName} logo`}
              className="max-h-12 md:max-h-14 max-w-[240px] w-auto object-contain"
              style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.4))" }}
            />
          </div>
        )}
        <div className="flex items-center gap-3 mb-4 text-[11px] uppercase tracking-[0.2em] text-white/60">
          <span>{clientName}</span>
          <span className="w-1 h-1 rounded-full bg-white/40" />
          <span>{monthLabel || month}</span>
          <span className="w-1 h-1 rounded-full bg-white/40" />
          <span>Performance Intelligence</span>
        </div>

        {loadingIntel ? (
          <div className="h-12 w-2/3 bg-white/5 rounded animate-pulse mb-3" />
        ) : (
          <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-[1.1] mb-3 fade-up-delay-1" style={{ fontFamily: "Varela Round, sans-serif" }} data-testid="pi-hero-headline">
            {headline || `${clientName} — ${monthLabel || month}`}
          </h1>
        )}
        {summary && (
          <p className="hero-subline text-sm md:text-base text-white/70 mb-6 fade-up-delay-2 max-w-3xl" data-testid="pi-hero-subline">
            {summary}
          </p>
        )}

        <div className="flex flex-wrap gap-2 fade-up-delay-3">
          <KpiTileMini
            label="Revenue"
            value={sh.revenue != null ? <AnimatedCurrency value={sh.revenue} currency={cur} /> : "—"}
            source="shopify"
          />
          <KpiTileMini
            label="Orders"
            value={sh.orders != null ? <AnimatedNumber value={sh.orders} /> : "—"}
            source="shopify"
          />
          <KpiTileMini
            label="AOV"
            value={sh.aov != null ? <AnimatedCurrency value={sh.aov} currency={cur} /> : "—"}
            source="shopify"
            benchmark={bm.aov}
          />
          <KpiTileMini
            label="Ad Spend"
            value={meta.ad_spend != null ? <AnimatedCurrency value={meta.ad_spend} currency={meta.currency || cur} /> : "—"}
            source="meta"
          />
          <KpiTileMini
            label="Blended ROAS"
            value={bl.roas != null ? `${bl.roas}x` : "—"}
            source="blended"
            sub="Shopify ÷ Meta"
            benchmark={bm.roas}
          />
          <KpiTileMini
            label="Sessions"
            value={ga.sessions != null ? <AnimatedNumber value={ga.sessions} /> : "—"}
            source="ga4"
          />
          <KpiTileMini
            label="Users"
            value={ga.users != null ? <AnimatedNumber value={ga.users} /> : "—"}
            source="ga4"
          />
          <KpiTileMini
            label="CR"
            value={ga.conversion_rate != null ? <AnimatedPct value={ga.conversion_rate} /> : "—"}
            source="ga4"
            benchmark={ga4Benchmark("conversion_rate", ga.conversion_rate)}
          />
          <KpiTileMini
            label="Bounce"
            value={ga.bounce_rate != null ? fmtPct(ga.bounce_rate) : "—"}
            source="ga4"
            benchmark={ga4Benchmark("bounce_rate", ga.bounce_rate, true)}
          />
          <KpiTileMini
            label="Engagement"
            value={ga.engagement_rate != null ? fmtPct(ga.engagement_rate) : "—"}
            source="ga4"
            benchmark={ga4Benchmark("engagement_rate", ga.engagement_rate)}
          />
          <KpiTileMini
            label="Avg Session"
            value={fmtDuration(ga.avg_session_duration)}
            source="ga4"
          />
        </div>

        {/* ── Executive Intelligence Cards (Opportunity / Risk / Recommendation) ── */}
        {intel && (intel.opportunity || intel.risk || intel.recommendation) && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 fade-up-delay-3" data-testid="pi-hero-insight-cards">
            <InsightCard
              kind="opportunity"
              data={intel.opportunity}
              accent="#00E676"
              icon="↑"
              footerLabel="Estimated impact"
              footerKey="estimated_impact"
              testid="hero-card-opportunity"
            />
            <InsightCard
              kind="risk"
              data={intel.risk}
              accent="#FF6D00"
              icon="!"
              footerLabel="Severity"
              footerKey="severity"
              testid="hero-card-risk"
            />
            <InsightCard
              kind="recommendation"
              data={intel.recommendation}
              accent="#FFEB00"
              icon="→"
              footerLabel="Effort · Priority"
              footerKey="_recommendation_footer"
              testid="hero-card-recommendation"
            />
          </div>
        )}
        {intel?.confidence && (
          <div className="mt-3 text-[10px] uppercase tracking-wider text-white/40 flex items-center gap-2" data-testid="pi-hero-confidence">
            <span>AI Confidence:</span>
            <span
              className="px-2 py-0.5 rounded"
              style={{
                background: intel.confidence === "high" ? "#00E67622" : intel.confidence === "medium" ? "#FFEB0022" : "#71717A22",
                color: intel.confidence === "high" ? "#00E676" : intel.confidence === "medium" ? "#FFEB00" : "#A1A1AA",
              }}
            >
              {intel.confidence}
            </span>
            {intel.cached && <span className="text-white/30">· cached</span>}
          </div>
        )}
      </div>
    </section>
  );
};

export default PerformanceHero;
